import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Heart } from "lucide-react";

import { ELLA_DIFFERENCE, FEATURES, HER_CRAFT, REVIEWS } from "@/lib/content";
import { GOOGLE_REVIEWS_URL } from "@/lib/site";
import { jsonLdScript, pageHead, PAGE_SEO, webPageGraph } from "@/lib/seo";
import { avifSrcSet, photos } from "@/lib/photos";

import { Reveal } from "@/components/site/reveal";
import { Photo } from "@/components/site/photo";
import { Eyebrow } from "@/components/site/eyebrow";
import { ReviewCard } from "@/components/site/review-card";

const PORTRAIT_SIZES = "(min-width: 1024px) 40vw, 100vw";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    ...pageHead({
      ...PAGE_SEO.about,
      preload: {
        href: photos.ella.avif,
        imageSrcSet: avifSrcSet(photos.ella),
        imageSizes: PORTRAIT_SIZES,
      },
      scripts: [jsonLdScript(webPageGraph(PAGE_SEO.about))],
    }),
  }),
});

function AboutPage() {
  return (
    <main id="main" tabIndex={-1}>
      <section className="relative overflow-hidden bg-secondary py-16 sm:py-24 lg:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-32 bottom-0 size-[26rem] rounded-full bg-rose-tint/50 blur-3xl"
        />
        <div className="relative mx-auto grid max-w-6xl items-center gap-16 px-5 lg:grid-cols-[0.85fr_1fr] lg:px-10">
          <Reveal>
            <div className="rounded-[2rem] border border-gold/40 bg-card p-4 shadow-[var(--shadow-lift)]">
              <Photo
                {...photos.ella}
                alt="Ella Wise smiling with her granddaughter"
                eager
                sizes={PORTRAIT_SIZES}
                className="aspect-[4/5] w-full rounded-[1.4rem] object-cover"
              />
              <div className="px-3 py-5 text-center">
                <p className="font-display text-xl">Ella Wise</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Owner &amp; Maker, Doll Clothes by Ella Wise
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <Eyebrow>MEET THE MAKER</Eyebrow>
            <h1 className="mt-5 text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              Made by hand. Made with{" "}
              <em className="accent-underline font-display italic text-primary">heart.</em>
            </h1>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Ella loves working with doll items and creates every piece herself — knitting,
              crocheting, and sewing outfits that bring smiles to children and grandparents alike.
              There's no team behind her and no workshop full of machines: if it carries her name,
              it passed through her hands from the first stitch to the final trim.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Based in Midland, Michigan, she designs new styles that feel special enough for
              gifting and durable enough for everyday play. Most of what she makes ends up as a
              gift — a birthday dress from a parent, an heirloom set from a grandmother, a bridal
              gown to mark a family wedding — so she keeps her prices affordable and her finishing
              boutique-quality.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Everything is sized for 18-inch American dolls, and she happily takes{" "}
              <Link to="/personalization" className="text-gold-ink underline underline-offset-4">
                made-to-order requests
              </Link>{" "}
              when someone has a color, theme, or occasion in mind.
            </p>


            <ul className="mt-8 flex flex-wrap gap-2.5">
              {["Midland, Michigan", "Knit • Crochet • Sew", "Handmade Gifts"].map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-gold/50 bg-card px-4 py-2 text-xs font-medium tracking-wide text-foreground"
                >
                  {t}
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              className="mt-9 inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Say Hello to Ella <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>HER CRAFT</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Three techniques,{" "}
              <em className="accent-underline font-display italic text-primary">one maker.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Most makers pick one. Ella knits, crochets, and sews — and chooses whichever suits the
              piece she's making, or combines them in a single outfit.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-5 md:grid-cols-3">
            {HER_CRAFT.map((c, i) => (
              <Reveal as="li" key={c.title} delay={i * 90}>
                <div className="h-full rounded-3xl bg-secondary p-7">
                  <h3 className="font-display text-xl">{c.title}</h3>
                  <div className="mt-4 stitch-rule text-primary" aria-hidden="true" />
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-plum py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>THE ELLA WISE DIFFERENCE</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              What you get when one person{" "}
              <em className="font-display italic text-gold">makes it all.</em>
            </h2>
          </Reveal>

          <ul className="mt-14 grid gap-7 md:grid-cols-3">
            {ELLA_DIFFERENCE.map((d, i) => (
              <Reveal as="li" key={d.title} delay={i * 90}>
                <div className="h-full rounded-3xl bg-plum-deep/40 p-7">
                  <h3 className="font-display text-xl">{d.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-plum-foreground/75">{d.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>



      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-2 lg:px-10">
          <Reveal className="relative">
            <Photo
              {...photos.cream}
              alt="Cream crochet doll gown with pink ribbon, bonnet, lace slip and booties laid out together"
              sizes="(min-width: 1024px) 45vw, 100vw"
              className="aspect-[4/5] w-full rounded-[2rem] object-cover shadow-[var(--shadow-lift)]"
            />
            <blockquote className="absolute -bottom-8 left-4 max-w-[20rem] rounded-3xl border border-border/70 bg-card p-6 shadow-[var(--shadow-lift)] sm:left-auto sm:-right-6">
              <Heart className="size-5 text-primary" aria-hidden="true" />
              <p className="mt-3 font-display text-lg italic leading-snug">
                “Every outfit is stitched like it's for my own family.”
              </p>
            </blockquote>
          </Reveal>

          <Reveal delay={120} className="mt-16 lg:mt-0">
            <Eyebrow>WHY DOLL CLOTHES BY ELLA WISE</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Boutique quality.{" "}
              <em className="accent-underline font-display italic text-primary">Heirloom</em> care.
            </h2>

            <ul className="mt-10 grid gap-5 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <li
                  key={f.title}
                  className="rounded-3xl border border-border/70 bg-card p-6 shadow-[var(--shadow-soft)]"
                >
                  <h3 className="font-display text-lg">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.copy}</p>
                </li>
              ))}
            </ul>

            <Link
              to="/collection"
              className="mt-10 inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Browse All Items <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section id="reviews" className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>CUSTOMER LOVE</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Little <em className="accent-underline font-display italic text-primary">smiles</em>{" "}
              say it all.
            </h2>
          </Reveal>

          <ul className="mt-14 grid gap-7 md:grid-cols-2">
            {REVIEWS.map((r, i) => (
              <ReviewCard key={r.name} quote={r.quote} name={r.name} delay={i * 100} />
            ))}
          </ul>

          <Reveal className="mt-10">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-primary/50 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
            >
              Read More Reviews <ArrowRight className="size-4" aria-hidden="true" />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
